import { ObservableMap } from '@stencil/store';
import {
    ApolloThemeType,
    defaultTheme,
    getApolloThemeFromTheme,
    getCachedTheme,
    getUserTheme,
    isBrowserIE,
    setTheme,
    setUserTheme,
    ThemeType,
    validateThemeCode,
} from '@uipath/portal-shell-util';

import { ThemeEvent } from '../models/appInsights/events/theme.events';
import { State } from '../store/types/state';
import {
    isMsi,
    isStandalone,
} from './EnvironmentUtil';
import { EventHandlersInstanceResolver } from './events/eventHandlersInstanceResolver';
import GlobalEventEmitter from './events/globalEventEmitters';
import { Features } from './featureFlags/featureFlagConstants';
import { getFeatureFlag } from './featureFlags/featureFlags';
import { isFalsyOrWhitespace } from './helper';
import Logger from './logger/logger';
import { getOrganizationStandardUrl } from './orgStandardUrlHelper';
import {
    getAccessToken,
    sessionStore,
} from './session/sessionStore';

export async function setPortalShellUserTheme(store: ObservableMap<State>, accessToken: string) {
    if (!accessToken) {
        return;
    }

    const cachedTheme = getCachedTheme();
    const validOverrideTheme = validateThemeCode(store.get('overrideTheme'));
    Logger.info('theme', `cachedTheme: ${JSON.stringify(cachedTheme)}, validOverrideTheme: ${validOverrideTheme}`, { state: store.state });

    const organizationStandardUrl = getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true });
    if (!validOverrideTheme && organizationStandardUrl) {
        const msi = isMsi();
        const userTheme = await getUserTheme(
            organizationStandardUrl,
            accessToken,
            sessionStore.get('organizationId'),
            sessionStore.get('userId'),
            msi,
        ) as ThemeType | null;

        if (!userTheme) {
            Logger.logCustomEvent(ThemeEvent.SetCachedThemeAsSelectedThemeOnInit, {
                cachedTheme,
                isSettingUserTheme: getFeatureFlag(Features.EnableUserTheme.name) && !isStandalone(),
            });
            await setSelectedTheme(store, cachedTheme, {
                accessToken,
                shouldBroadcast: false,
            });
        } else if (userTheme && cachedTheme !== userTheme) {
            Logger.logCustomEvent(ThemeEvent.SetUserThemeAsCachedThemeOnInit, { userTheme });
            setThemeNoBroadcast(store, userTheme);
        }
    }
}

export async function setSelectedTheme(store: ObservableMap<State>, theme: ThemeType, {
    accessToken = undefined,
    shouldBroadcast = true,
}: {
    accessToken?: string;
    shouldBroadcast?: boolean;
} = {}) {
    if (!getFeatureFlag(Features.EnableUserTheme.name) || isStandalone()) {
        setTheme(theme);
        return;
    }

    if (!shouldBroadcast) {
        setStoreTheme(store, theme);
    }
    await setUserTheme(
        theme,
        getOrganizationStandardUrl({ shouldIncludeAccountNameOnK8s: true }),
        getAccessToken(accessToken),
        sessionStore.get('organizationId'),
        sessionStore.get('userId'),
        isMsi(),
        { shouldBroadcast },
    );
}

function getDarkMediaQueryList(): MediaQueryList {
    return window.matchMedia('(prefers-color-scheme: dark)');
}

function handleThemeMediaChange(store: ObservableMap<State>) {
    return (e: MediaQueryListEvent): void => {
        store.forceUpdate('selectedTheme');
        setApolloTheme(store, e.matches ? 'dark' : 'light');
    };
}

export function addThemeMediaListener(store: ObservableMap<State>) {
    if (!isBrowserIE()) {
        const darkMediaQueryList = getDarkMediaQueryList();
        if (darkMediaQueryList.addEventListener) {
            darkMediaQueryList.addEventListener('change', handleThemeMediaChange(store));
        }
    }
}

export function removeThemeMediaListener(store: ObservableMap<State>) {
    if (!isBrowserIE()) {
        const darkMediaQueryList = getDarkMediaQueryList();
        if (darkMediaQueryList.removeEventListener) {
            darkMediaQueryList.removeEventListener('change', handleThemeMediaChange(store));
        }
    }
}

export function getTheme(store: ObservableMap<State>, override?: ThemeType) {
    const apolloTheme = getApolloThemeFromTheme(store.get('selectedTheme') || getCachedTheme() || defaultTheme);
    const validThemeOverride = override && validateThemeCode(override);
    const shouldUseOverride = validThemeOverride && validThemeOverride !== 'autoTheme';
    return {
        dark: shouldUseOverride ? override === 'dark' : apolloTheme === 'dark',
        light: shouldUseOverride ? override === 'light' : apolloTheme === 'light',
    };
}

export function setApolloTheme(store: ObservableMap<State>, apolloTheme: ApolloThemeType) {
    Logger.debug('store', `set apollo theme: ${apolloTheme}`);
    if (isFalsyOrWhitespace(apolloTheme)) {
        return;
    }

    const themeEvent = { selectedThemeId: apolloTheme };
    const defaultEmitter = store.get('themeChangedEmitter');
    GlobalEventEmitter.emitThemeChanged(
        themeEvent,
        defaultEmitter ? () => defaultEmitter.emit(themeEvent) : undefined,
    );
    EventHandlersInstanceResolver.Instance?.onThemeChanged?.(themeEvent);

    // update body class list based on theme.
    // note this works only for angular and not for react
    Logger.debug('store', `set body theme: ${apolloTheme}`);
    document.body?.classList?.remove('light', 'dark');
    document.body?.classList?.add(apolloTheme);
}

export function setThemeNoBroadcast(store: ObservableMap<State>, theme: ThemeType) {
    setStoreTheme(store, theme);
    setTheme(theme, { shouldBroadcast: false });
}

export function setStoreTheme(store: ObservableMap<State>, theme: ThemeType) {
    theme = validateThemeCode(theme) ?? defaultTheme;
    const selectedTheme = store.get('selectedTheme');
    const apolloTheme = getApolloThemeFromTheme(theme);

    if (selectedTheme === theme) {
        Logger.debug('store', `theme has not changed: ${selectedTheme} === ${theme}`);
    } else {
        if (!selectedTheme) {
            const selectedThemeEvent = { selectedThemeId: apolloTheme };
            GlobalEventEmitter.emitThemeLoaded(selectedThemeEvent);
            EventHandlersInstanceResolver.Instance?.onThemeLoaded?.(selectedThemeEvent);
        }
        store.set('selectedTheme', theme);
        Logger.info('store', `theme has changed: ${selectedTheme} to ${theme}`);

        if (theme === 'autoTheme') {
            addThemeMediaListener(store);
        } else {
            removeThemeMediaListener(store);
        }
    }

    setApolloTheme(store, apolloTheme);
}
